
import * as React from 'react';
import { useState } from 'react';

interface MonthlyLogsProps {
  logs: any[];
  isAdmin?: boolean;
  currentUser?: any;
  userProfiles?: Record<string, string>;
  onUpdate?: () => void;
  onEdit?: (item: any) => void;
  onDelete?: (id: string) => void;
}

function UserBadge({ username }: { username?: string }) {
  if (!username) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 rounded-full text-xs font-medium border border-teal-100 dark:border-teal-700 whitespace-nowrap">
      <i className="ri-user-line text-xs"></i>
      {username}
    </span>
  );
}

// ─── 達成度・進捗度ミニバー ───────────────────────────────────────────────────
function RateMiniBar({ achievementRate, progressRate }: { achievementRate?: number; progressRate?: number }) {
  const ach = achievementRate ?? 0;
  const prog = progressRate ?? 0;
  if (ach === 0 && prog === 0) return null;

  const getColor = (v: number) => {
    if (v >= 80) return 'from-emerald-400 to-teal-500';
    if (v >= 50) return 'from-teal-400 to-cyan-500';
    if (v >= 30) return 'from-amber-400 to-orange-400';
    return 'from-rose-400 to-pink-400';
  };

  return (
    <div className="flex items-center gap-3 px-5 sm:px-6 pb-3 -mt-1">
      <div className="flex-1 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-500 dark:text-gray-400 w-14 flex-shrink-0 flex items-center gap-1">
            <i className="ri-trophy-line text-teal-500 text-xs"></i>達成度
          </span>
          <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${getColor(ach)} rounded-full transition-all duration-700`}
              style={{ width: `${ach}%` }}
            ></div>
          </div>
          <span className="text-[10px] font-bold text-teal-600 dark:text-teal-400 w-8 text-right">{ach}%</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-500 dark:text-gray-400 w-14 flex-shrink-0 flex items-center gap-1">
            <i className="ri-rocket-line text-emerald-500 text-xs"></i>進捗度
          </span>
          <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full bg-gradient-to-r ${getColor(prog)} rounded-full transition-all duration-700`}
              style={{ width: `${prog}%` }}
            ></div>
          </div>
          <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 w-8 text-right">{prog}%</span>
        </div>
      </div>
    </div>
  );
}

export default function MonthlyLogs({
  logs = [],
  isAdmin = false,
  currentUser,
  userProfiles = {},
  onUpdate,
  onEdit,
  onDelete,
}: MonthlyLogsProps) {
  const [selectedTag, setSelectedTag] = useState<string>('all');
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(
    new Set(logs?.length ? [logs[0]?.id] : [])
  );
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const allTags = React.useMemo(
    () =>
      Array.from(
        new Set(
          logs.flatMap((log) =>
            Array.isArray(log?.tags) ? (log.tags as string[]) : []
          )
        )
      ),
    [logs]
  );

  const filteredLogs = React.useMemo(() => {
    if (selectedTag === 'all') return logs;
    return logs.filter(
      (log) =>
        Array.isArray(log?.tags) && (log.tags as string[]).includes(selectedTag)
    );
  }, [logs, selectedTag]);

  const toggleLog = (id: string) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDelete = (id: string) => {
    if (onDelete) {
      try { onDelete(id); } catch (e) { console.error('Failed to delete log:', e); }
      setDeleteConfirm(null);
    }
  };

  const canModify = (item: any) => {
    if (isAdmin) return true;
    if (!currentUser) return false;
    return item?.user_id === currentUser.id;
  };

  if (!logs || logs.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-50 dark:bg-teal-900/40 rounded-lg">
            <i className="ri-calendar-line text-teal-600 dark:text-teal-400"></i>
          </div>
          進捗ログ（月次）
        </h2>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-4">
          進捗ログがまだ登録されていません。
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-50 dark:bg-teal-900/40 rounded-lg">
            <i className="ri-calendar-line text-teal-600 dark:text-teal-400"></i>
          </div>
          進捗ログ（月次）
        </h2>
        {allTags.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => setSelectedTag('all')}
              className={`px-3 py-1 text-xs rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                selectedTag === 'all'
                  ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              すべて
            </button>
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`px-3 py-1 text-xs rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  selectedTag === tag
                    ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {filteredLogs.map((log, index) => {
          const isExpanded = expandedLogs.has(log.id);
          const isLatest = index === 0 && selectedTag === 'all';
          const userCanModify = canModify(log);
          const username = log.user_profiles?.username || null;

          return (
            <div
              key={log.id}
              className={`bg-white dark:bg-gray-800 rounded-xl border overflow-hidden transition-all ${
                isLatest ? 'border-teal-300 dark:border-teal-600 shadow-md' : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              {/* Header row */}
              <button
                onClick={() => toggleLog(log.id)}
                className="w-full px-5 sm:px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-600 rounded-lg text-white">
                      <i className="ri-calendar-check-line text-sm"></i>
                    </div>
                    <span className="font-bold text-gray-800 dark:text-white text-sm">
                      {log.period}
                    </span>
                  </div>
                  {isLatest && (
                    <span className="bg-teal-50 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 text-xs font-medium px-2.5 py-0.5 rounded-full whitespace-nowrap">
                      最新
                    </span>
                  )}
                  {username && <UserBadge username={username} />}
                  <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
                    {log.objective}
                  </span>
                  {Array.isArray(log.tags) && log.tags.length > 0 && (
                    <div className="hidden sm:flex gap-1">
                      {log.tags.map((tag: string, idx: number) => (
                        <span
                          key={idx}
                          className="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded text-xs whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {userCanModify && onEdit && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onEdit(log); }}
                      className="w-8 h-8 flex items-center justify-center text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/40 rounded-lg transition-colors cursor-pointer"
                      title="編集"
                    >
                      <i className="ri-edit-line text-base"></i>
                    </button>
                  )}
                  {userCanModify && onDelete && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm(log.id); }}
                      className="w-8 h-8 flex items-center justify-center text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors cursor-pointer"
                      title="削除"
                    >
                      <i className="ri-delete-bin-line text-base"></i>
                    </button>
                  )}
                  <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-xl text-gray-400 dark:text-gray-500 flex-shrink-0`}></i>
                </div>
              </button>

              {/* 達成度・進捗度ミニバー */}
              <RateMiniBar
                achievementRate={log.achievement_rate}
                progressRate={log.progress_rate}
              />

              {/* Expanded content */}
              {isExpanded && (
                <div className="border-t border-gray-100 dark:border-gray-700 px-5 sm:px-6 py-5 space-y-5">
                  <div className="bg-teal-50/50 dark:bg-teal-900/20 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-teal-700 dark:text-teal-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <i className="ri-focus-3-line"></i>今月の目的
                    </h4>
                    <p className="text-sm text-gray-800 dark:text-gray-200 font-medium">{log.objective}</p>
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                      <i className="ri-checkbox-circle-line text-emerald-500"></i>実施内容
                    </h4>
                    <div className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">{log.actions}</div>
                  </div>

                  {Array.isArray(log.outputs) && log.outputs.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                        <i className="ri-file-list-line text-teal-500"></i>生成物
                      </h4>
                      <div className="space-y-2">
                        {log.outputs.map((output: any, idx: number) => (
                          <div key={idx} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 flex items-center gap-3 border border-gray-100 dark:border-gray-600">
                            <div className="w-8 h-8 flex items-center justify-center bg-teal-50 dark:bg-teal-900/40 rounded-lg flex-shrink-0">
                              <i className="ri-file-text-line text-teal-600 dark:text-teal-400 text-sm"></i>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{output.name}</p>
                              {output.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{output.description}</p>
                              )}
                            </div>
                            {output.link && (
                              <a href={output.link} target="_blank" rel="noopener noreferrer" className="text-teal-600 dark:text-teal-400 hover:text-teal-700 whitespace-nowrap cursor-pointer">
                                <i className="ri-external-link-line"></i>
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {log.adjustments && (
                    <div className="bg-amber-50/60 dark:bg-amber-900/10 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <i className="ri-error-warning-line"></i>詰まり・修正点
                      </h4>
                      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">{log.adjustments}</p>
                    </div>
                  )}

                  {Array.isArray(log.next_todo) && log.next_todo.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                        <i className="ri-todo-line text-teal-500"></i>次のToDo
                      </h4>
                      <div className="space-y-2">
                        {log.next_todo.map((todo: string, idx: number) => (
                          <div key={idx} className="flex items-start gap-2.5">
                            <div className="flex-shrink-0 w-5 h-5 bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5">
                              {idx + 1}
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed flex-1">{todo}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredLogs.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
          <i className="ri-filter-off-line text-3xl text-gray-300 dark:text-gray-600 mb-2"></i>
          <p className="text-gray-500 dark:text-gray-400 text-sm">選択したタグに該当するログがありません。</p>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-rose-100 dark:bg-rose-900/40 rounded-full">
                <i className="ri-alert-line text-rose-600 dark:text-rose-400 text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">削除の確認</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">この操作は取り消せません</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-6">この月次ログを削除してもよろしいですか？</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                キャンセル
              </button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
